import _ from 'lodash'
import moment from 'moment'
import { useMemo } from 'react'

import COLORS from 'pared/constants/colors'

import useSelectOptions, {
  ISelectOptionsDataType,
} from '../common/useSelectOptions'

type IColorType =
  | string
  | {
      range: number[]
      colors: string[]
      value?: `<%- ${string} %>`
    }

interface IColumnType {
  key: string
  title: string
  align?: 'left' | 'center' | 'right'
  background?: IColorType
}

interface IPriceColumnType extends IColumnType {
  children?: {
    key: string
    title: string
    align?: 'left' | 'center' | 'right'
    width?: string
    sortable?: boolean
    decimal?: number
    color?: IColorType
    unit?: 'dollar' | 'cent'
    background?: IColorType
  }[]
}
interface IPercentColumnType extends IColumnType {
  children?: {
    key: string
    title: string
    align?: 'left' | 'center' | 'right'
    width?: string
    sortable?: boolean
    decimal?: number
    color?: IColorType
    isDecimal?: boolean
  }[]
}

export interface IDataType {
  ghaiPopLateNightMetricOptions?: ISelectOptionsDataType
  ghaiPopLateNightTableColumns?: {
    price?: IPriceColumnType[]
    percent?: IPercentColumnType[]
    hours?: IPriceColumnType[]
  }
  ghaiPopLateNightCsvColumns?: {
    key: string
    title: string
  }[]
}

const LABOR_METRICS: Record<
  string,
  {
    displayName: string
    csvDisplayName: string
    type: 'price' | 'percent' | 'hours'
  }
> = {
  hourly_labor_sales: {
    displayName: 'Average Sales $',
    csvDisplayName: 'Average  Sales $',
    type: 'price',
  },
  hourly_labor_cost: {
    displayName: 'Average Labor $',
    csvDisplayName: 'Average Labor $',
    type: 'price',
  },
  hourly_labor_percent: {
    displayName: 'Average Labor %',
    csvDisplayName: 'Average Labor %',
    type: 'percent',
  },
  hourly_labor_minutes: {
    displayName: 'Average Labor Hours (#)',
    csvDisplayName: 'Average Labor Minutes',
    type: 'hours',
  },
}

const useGhaiPopLateNightConfigs = () => {
  const metricOptions = {
    defaultId: [['hourly_labor_sales']],
    options: Object.entries(LABOR_METRICS).map(([id, { displayName }]) => ({
      id,
      displayName,
    })),
  }

  const { selectOptions } = useSelectOptions({ options: { metricOptions } })
  const currentSelectedMetric =
    selectOptions?.metricOptions?.value?.[0]?.[0] || 'unknown'

  return useMemo(
    () => ({
      ghaiPopLateNightMetricOptions: selectOptions?.metricOptions,
      ghaiPopLateNightTableColumns: {
        ...(LABOR_METRICS[currentSelectedMetric]?.type === 'price'
          ? {
              price: [1, 2, 3, 4, 5, 6, 0].map((i, n) => {
                const weekdayStr = moment().day(i).format('dddd')

                return {
                  key: weekdayStr.toLowerCase(),
                  title: weekdayStr,
                  align: 'center',
                  background: n % 2 === 0 ? `${COLORS.Porcelain}` : undefined,
                  children: [21, 22, 23, 24, 25, 26].map((hour) => ({
                    key: _.camelCase(
                      `weekday_${i}_late_night_${hour}_${currentSelectedMetric}`,
                    ),
                    title: `${moment(hour > 23 ? hour - 24 : hour, 'H').format(
                      'h:mm A',
                    )}`,
                    width: '110px',
                    align: 'center',
                    sortable: true,
                    color: 'black',
                    unit: 'dollar',
                    ...(currentSelectedMetric === 'hourly_labor_sales'
                      ? {
                          background: {
                            colors: [
                              n % 2 === 0
                                ? `${COLORS.Porcelain}`
                                : `${COLORS.Salt}`,
                              'hsl(0, 80%, 70%)',
                              'hsl(120, 75%, 85%)',
                              'hsl(120, 60%, 55%)',
                            ],
                            range: [0, 1, 100, 1000],
                            value: `<%- value === null ? 0 : (value < 100 ? 1 : value) %>`,
                          },
                        }
                      : {
                          background: {
                            colors: [
                              n % 2 === 0
                                ? `${COLORS.Porcelain}`
                                : `${COLORS.Salt}`,
                              'hsl(0, 80%, 70%)',
                            ],
                            range: [0, 200],
                            value: `<%- value || 0 %>`,
                          },
                        }),
                  })),
                }
              }),
            }
          : {}),
        ...(LABOR_METRICS[currentSelectedMetric]?.type === 'percent'
          ? {
              percent: [1, 2, 3, 4, 5, 6, 0].map((i, n) => {
                const weekdayStr = moment().day(i).format('dddd')

                return {
                  key: weekdayStr.toLowerCase(),
                  title: weekdayStr,
                  align: 'center',
                  background: n % 2 === 0 ? `${COLORS.Porcelain}` : undefined,
                  children: [21, 22, 23, 24, 25, 26].map((hour) => ({
                    key: _.camelCase(
                      `weekday_${i}_late_night_${hour}_${currentSelectedMetric}`,
                    ),
                    title: `${moment(hour > 23 ? hour - 24 : hour, 'H').format(
                      'h:mm A',
                    )}`,
                    width: '110px',
                    align: 'center',
                    sortable: true,
                    decimal: 1,
                    background: {
                      colors: [
                        n % 2 === 0 ? `${COLORS.Porcelain}` : `${COLORS.Salt}`,
                        'hsl(0, 80%, 70%)',
                        'hsl(0, 100%, 65%)',
                      ],
                      range: [0, 100, 101],
                      value: `<%- value === null ? 0 : (value > 100 ? 101 : value) %>`,
                    },
                  })),
                }
              }),
            }
          : {}),
        ...(LABOR_METRICS[currentSelectedMetric]?.type === 'hours'
          ? {
              hours: [1, 2, 3, 4, 5, 6, 0].map((i, n) => {
                const weekdayStr = moment().day(i).format('dddd')

                return {
                  key: weekdayStr.toLowerCase(),
                  title: weekdayStr,
                  align: 'center',
                  background: n % 2 === 0 ? `${COLORS.Porcelain}` : undefined,
                  children: [21, 22, 23, 24, 25, 26].map((hour) => ({
                    key: _.camelCase(
                      `weekday_${i}_late_night_${hour}_${currentSelectedMetric}`,
                    ),
                    title: `${moment(hour > 23 ? hour - 24 : hour, 'H').format(
                      'h:mm A',
                    )}`,
                    width: '110px',
                    align: 'center',
                    sortable: true,
                    calculateFrom: 'minutes',
                    decimal: 2,
                    background: {
                      colors: [
                        n % 2 === 0 ? `${COLORS.Porcelain}` : `${COLORS.Salt}`,
                        'hsl(0, 80%, 70%)',
                        'hsl(0, 100%, 65%)',
                      ],
                      range: [0, 600, 601],
                      value: `<%- value === null ? 0 : (value > 600 ? 601 : value) %>`,
                    },
                  })),
                }
              }),
            }
          : {}),
      },
      ghaiPopLateNightCsvColumns: [1, 2, 3, 4, 5, 6, 0]
        .map((day) =>
          [21, 22, 23, 24, 25, 26]
            .map((hour) => ({
              key: _.camelCase(
                `weekday_${day}_late_night_${hour}_${currentSelectedMetric}`,
              ),
              title: `${moment().weekday(day).format('dddd')} ${
                LABOR_METRICS[currentSelectedMetric].csvDisplayName
              } ${moment(hour > 23 ? hour - 24 : hour, 'H').format('h:mm A')}`,
            }))
            .flat(),
        )
        .flat(),
    }),
    [selectOptions, currentSelectedMetric],
  )
}

export default useGhaiPopLateNightConfigs
